import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const Wrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 35px;
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 12px 8px; /* increased from 10px to 16px */
  }

  th {
    background: #87ceeb;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }
`;


const RiskCell = styled.td`
  background-color: ${({ risk }) =>
    risk === 'high' ? 'rgba(255, 0, 0, 0.1)' :
    risk === 'mid' ? 'rgba(255, 165, 0, 0.1)' : '#f0f0f0'};
  font-weight: ${({ risk }) => (risk !== 'low' ? 'bold' : 'normal')};
`;

const StyledTrend = styled.span`
  font-weight: bold;
  color: ${({ trend }) =>
    trend > 0 ? 'green' :
    trend < 0 ? 'red' : '#666'};
`;

const WojewodztwaRiskReport = ({ salesData, clientsData }) => {
  const [riskData, setRiskData] = useState([]);
  const [selectedWoj, setSelectedWoj] = useState("Wszyscy");
  const [availableWoj, setAvailableWoj] = useState([]);

  useEffect(() => {
    if (!salesData.length || !clientsData.length) return;

    const latestDate = salesData.reduce((latest, sale) => {
      const dateStr = `${sale.Rok}-${sale.Miesiac.toString().padStart(2, "0")}`;
      return !latest || latest < dateStr ? dateStr : latest;
    }, null);
    if (!latestDate) return;
    const [latestYear, latestMonth] = latestDate.split("-").map(Number);

    const lastPurchases = {};
    salesData.forEach((sale) => {
      const nip = sale.NrKontrahenta?.trim();
      const year = parseInt(sale.Rok, 10);
      const month = parseInt(sale.Miesiac, 10);
      const key = `${year}-${month.toString().padStart(2, "0")}`;
      if (!lastPurchases[nip] || lastPurchases[nip] < key) {
        lastPurchases[nip] = key;
      }
    });

    const customerInactivity = {};
    Object.entries(lastPurchases).forEach(([nip, lastDate]) => {
      const [lastYear, lastMonth] = lastDate.split("-").map(Number);
      const monthsDiff = (latestYear - lastYear) * 12 + (latestMonth - lastMonth);
      customerInactivity[nip] = monthsDiff;
    });

    const wojData = {};
    clientsData.forEach((client) => {
      const nip = client.nip?.trim();
      const woj = client.wojewodztwo || "Nieznane";
      const monthsInactive = customerInactivity[nip];

      if (!monthsInactive && monthsInactive !== 0) return;

      if (!wojData[woj]) wojData[woj] = { woj, midRisk: 0, highRisk: 0, previousRisk: 0 };

      if (monthsInactive >= 3) {
        if (monthsInactive >= 6) wojData[woj].highRisk += 1;
        else wojData[woj].midRisk += 1;
      }

      if (monthsInactive >= 0 && monthsInactive <= 3) {
        wojData[woj].previousRisk += 1;
      }
    });

    const result = Object.values(wojData)
      .filter(w => w.midRisk > 0 || w.highRisk > 0)
      .map(w => ({
        ...w,
        trend: (w.midRisk + w.highRisk) - w.previousRisk
      }))
      .sort((a, b) => (b.highRisk + b.midRisk) - (a.highRisk + a.midRisk));

    setRiskData(result);
    setAvailableWoj(["Wszyscy", ...result.map(w => w.woj)]);
  }, [salesData, clientsData]);

  const displayedData = selectedWoj === "Wszyscy"
    ? riskData
    : riskData.filter(item => item.woj === selectedWoj);

  return (
    <Wrapper>
      <TitleWrapper>
       Województwa z ryzykiem utraty sprzedaży
        <div data-tooltip-id="risk-tooltip" style={{ cursor: "pointer" }}>
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="risk-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Raport województw z ryzykiem</strong><br/>
            Pokazuje województwa z klientami<br/>
            którzy nie kupują od 3–6 m-cy (średnie ryzyko) lub<br/>
            6+ m-cy (wysokie). Trend pokazuje wzrost/spadek.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Województwo:</label>
          <Select value={selectedWoj} onChange={(e) => setSelectedWoj(e.target.value)}>
            {availableWoj.map((woj, index) => (
              <option key={index} value={woj}>{woj}</option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      <StyledTable>
        <thead>
          <tr>
            <th>Województwo</th>
            <th>Brak zakupów 3–6 m-cy</th>
            <th>Brak zakupów 6+ m-cy</th>
            <th>Trend</th>
          </tr>
        </thead>
        <tbody>
          {displayedData.length > 0 ? (
            displayedData.map((item, index) => (
              <tr key={index}>
                <td>{item.woj}</td>
                <RiskCell risk={item.highRisk > 5 ? 'high' : item.midRisk > 5 ? 'mid' : 'low'}>
                  {item.midRisk}
                </RiskCell>
                <RiskCell risk={item.highRisk > 5 ? 'high' : item.midRisk > 5 ? 'mid' : 'low'}>
                  {item.highRisk}
                </RiskCell>
                <td>
                  <StyledTrend trend={item.trend}>
                    {item.trend > 0 ? `↑ ${item.trend}` : item.trend < 0 ? `↓ ${Math.abs(item.trend)}` : `→ 0`}
                  </StyledTrend>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4">Brak danych o ryzyku w województwach.</td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </Wrapper>
  );
};

export default WojewodztwaRiskReport;
